import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Users, Clock, CheckCircle, XCircle, Eye, Filter, Search, Download, Calendar, ChevronLeft, ChevronRight, Loader2, Store } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { adminApi } from '@/services/api/admin';
import { getSellerUserStatusIcon } from '@/utils/renderIcons';
import { formatDate, getStatusSellerColor, tRequestSellerStatus } from '@/utils/formatters';
import { SellerRequest } from '@/services/types/domain';

interface AdminSellerRequestsProps {
    onNavigateBack?: () => void;
}

interface Filters {
    status?: string;
    search?: string;
    page?: number;
    per_page?: number;
}

const AdminSellerRequestsComponent: React.FC<AdminSellerRequestsProps> = ({
    onNavigateBack
}) => {
    const [requests, setRequests] = useState<SellerRequest[]>([]);
    const [pagination, setPagination] = useState({
        current_page: 1,
        per_page: 10,
        total: 0,
        last_page: 1,
        from: 0,
        to: 0
    });
    const [isLoading, setIsLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState<string>('all');
    const [selectedRequest, setSelectedRequest] = useState<SellerRequest | null>(null);
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [processingIds, setProcessingIds] = useState<Set<number>>(new Set());

    const searchTimeoutRef = useRef<NodeJS.Timeout>();
    const searchInputRef = useRef<HTMLInputElement>(null);

    // Función para cargar solicitudes con debounce
    const fetchRequests = useCallback(async (filters: Filters = {}) => {
        setIsLoading(true);
        try {
            const response = await adminApi.getSellerRequests({
                status: filters.status !== 'all' ? filters.status as 'pending' | 'verified' | 'rejected' : undefined,
                search: filters.search,
                page: filters.page || 1,
                per_page: filters.per_page || 10
            });

            setRequests(response.data);
            setPagination({
                current_page: response.meta.current_page,
                per_page: response.meta.per_page,
                total: response.meta.total,
                last_page: response.meta.last_page,
                from: response.meta.from,
                to: response.meta.to
            });

        } catch (error: any) {
            console.error('Error loading seller requests:', error);

            if (error?.response?.status === 403) {
                toast.error('No tienes permisos para ver las solicitudes');
            } else if (error?.response?.status === 500) {
                toast.error('Error del servidor. Inténtalo más tarde.');
            } else {
                toast.error('Error al cargar las solicitudes');
            }

            setRequests([]);
        } finally {
            setIsLoading(false);
        }
    }, []);

    // Cargar solicitudes inicialmente y cuando cambien los filtros principales
    useEffect(() => {
        const filters: Filters = {
            status: statusFilter,
            page: 1 // Resetear a primera página al cambiar filtros
        };

        fetchRequests(filters);
    }, [statusFilter, fetchRequests]);

    // Debounce para la búsqueda
    useEffect(() => {
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }

        searchTimeoutRef.current = setTimeout(() => {
            if (searchTerm.trim() !== '') {
                fetchRequests({
                    search: searchTerm,
                    status: statusFilter,
                    page: 1
                });
            } else if (searchTerm === '') {
                // Si se borra la búsqueda, recargar sin filtro de búsqueda
                fetchRequests({
                    status: statusFilter,
                    page: 1
                });
            }
        }, 500);

        return () => {
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
            }
        };
    }, [searchTerm, statusFilter, fetchRequests]);

    const handleApprove = async (requestId: number) => {
        if (!window.confirm('¿Estás seguro de aprobar esta solicitud?')) {
            return;
        }
        setProcessingIds(prev => new Set(prev).add(requestId));

        try {
            await adminApi.approveSeller(requestId);

            // Actualizar la lista
            setRequests(prev => prev.map(req =>
                req.id === requestId
                    ? { ...req, verification_status: 'verified', updated_at: new Date().toISOString() }
                    : req
            ));

            toast.success('Vendedor aprobado exitosamente');

        } catch (error: any) {
            console.error('Error approving seller:', error);
            if (error?.response?.status === 404) {
                toast.error('Solicitud no encontrada');
            } else if (error?.response?.status === 409) {
                toast.error('Esta solicitud ya fue procesada');
            } else {
                toast.error('Error al aprobar vendedor');
            }
        } finally {
            setProcessingIds(prev => {
                const newSet = new Set(prev);
                newSet.delete(requestId);
                return newSet;
            });
        }
    };

    const handleReject = async (requestId: number, reason?: string) => {
        if (!window.confirm('¿Estás seguro de rechazar esta solicitud?')) {
            return;
        }
        setProcessingIds(prev => new Set(prev).add(requestId));
        try {
            await adminApi.rejectSeller(requestId, reason);

            // Actualizar la lista
            setRequests(prev => prev.map(req =>
                req.id === requestId
                    ? { ...req, verification_status: 'rejected', updated_at: new Date().toISOString() }
                    : req
            ));

            toast.success('Solicitud rechazada');
        } catch (error: any) {
            console.error('Error rejecting seller:', error);
            if (error?.response?.status === 404) {
                toast.error('Solicitud no encontrada');
            } else if (error?.response?.status === 409) {
                toast.error('Esta solicitud ya fue procesada');
            } else {
                toast.error('Error al rechazar solicitud');
            }
        } finally {
            setProcessingIds(prev => {
                const newSet = new Set(prev);
                newSet.delete(requestId);
                return newSet;
            });
        }
    };

    const handlePageChange = (page: number) => {
        fetchRequests({
            status: statusFilter !== 'all' ? statusFilter : undefined,
            search: searchTerm || undefined,
            page: page,
            per_page: pagination.per_page
        });

        // Scroll to top
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handlePerPageChange = (perPage: number) => {
        fetchRequests({
            status: statusFilter !== 'all' ? statusFilter : undefined,
            search: searchTerm || undefined,
            page: 1,
            per_page: perPage
        });
    };

    const openDetailModal = (request: SellerRequest) => {
        setSelectedRequest(request);
        setShowDetailModal(true);
    };

    // Componente de paginación
    const PaginationControls = () => (
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-white">
            <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-700">
                    Mostrando {pagination.from} a {pagination.to} de {pagination.total} resultados
                </span>
                <select
                    value={pagination.per_page}
                    onChange={(e) => handlePerPageChange(Number(e.target.value))}
                    className="border border-gray-300 rounded-md px-2 py-1 text-sm"
                >
                    <option value={10}>10 por página</option>
                    <option value={25}>25 por página</option>
                    <option value={50}>50 por página</option>
                    <option value={100}>100 por página</option>
                </select>
            </div>

            <div className="flex items-center space-x-2">
                <button
                    onClick={() => handlePageChange(pagination.current_page - 1)}
                    disabled={pagination.current_page === 1}
                    className="p-1 rounded-md border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    <ChevronLeft className="w-4 h-4" />
                </button>

                {Array.from({ length: Math.min(5, pagination.last_page) }, (_, i) => {
                    const page = pagination.current_page <= 3
                        ? i + 1
                        : Math.min(pagination.current_page + i - 2, pagination.last_page);

                    return (
                        <button
                            key={page}
                            onClick={() => handlePageChange(page)}
                            className={`w-8 h-8 rounded-md text-sm ${pagination.current_page === page
                                ? 'bg-primary-600 text-white'
                                : 'border border-gray-300 text-gray-700 hover:bg-gray-50'
                                }`}
                        >
                            {page}
                        </button>
                    );
                })}

                <button
                    onClick={() => handlePageChange(pagination.current_page + 1)}
                    disabled={pagination.current_page === pagination.last_page}
                    className="p-1 rounded-md border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    <ChevronRight className="w-4 h-4" />
                </button>
            </div>
        </div>
    );

    // Estadísticas (se calculan de los datos actuales en la página)
    const pendingCount = requests.filter(r => r.verification_status === 'pending').length;
    const verifiedCount = requests.filter(r => r.verification_status === 'verified').length;
    const rejectedCount = requests.filter(r => r.verification_status === 'rejected').length;

    if (isLoading && requests.length === 0) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
                    <p className="text-gray-600">Cargando solicitudes de vendedores...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Header */}
                <div className="mb-8">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                        <div>
                            <h1 className="text-2xl font-bold text-gray-900 flex items-center">
                                <Users className="w-6 h-6 mr-2" />
                                Solicitudes de Vendedores
                            </h1>
                            <p className="mt-1 text-gray-600">
                                Gestiona las solicitudes para convertirse en vendedor
                            </p>
                        </div>
                        <div className="mt-4 sm:mt-0 flex space-x-3">
                            <button className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors">
                                <Download className="w-4 h-4 mr-2" />
                                Exportar
                            </button>
                            {onNavigateBack && (
                                <button
                                    onClick={onNavigateBack}
                                    className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 transition-colors"
                                >
                                    Volver al Dashboard
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                {/* Stats Cards */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                        <div className="flex items-center">
                            <div className="p-2 bg-yellow-100 rounded-lg">
                                <Clock className="w-6 h-6 text-yellow-600" />
                            </div>
                            <div className="ml-4">
                                <p className="text-sm font-medium text-gray-600">Pendientes</p>
                                <p className="text-2xl font-bold text-gray-900">{pendingCount}</p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                        <div className="flex items-center">
                            <div className="p-2 bg-green-100 rounded-lg">
                                <CheckCircle className="w-6 h-6 text-green-600" />
                            </div>
                            <div className="ml-4">
                                <p className="text-sm font-medium text-gray-600">Aprobados</p>
                                <p className="text-2xl font-bold text-gray-900">{verifiedCount}</p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                        <div className="flex items-center">
                            <div className="p-2 bg-red-100 rounded-lg">
                                <XCircle className="w-6 h-6 text-red-600" />
                            </div>
                            <div className="ml-4">
                                <p className="text-sm font-medium text-gray-600">Rechazados</p>
                                <p className="text-2xl font-bold text-gray-900">{rejectedCount}</p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                        <div className="flex items-center">
                            <div className="p-2 bg-blue-100 rounded-lg">
                                <Users className="w-6 h-6 text-blue-600" />
                            </div>
                            <div className="ml-4">
                                <p className="text-sm font-medium text-gray-600">Total</p>
                                <p className="text-2xl font-bold text-gray-900">{requests.length}</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Filters - Con debounce y mantenimiento de focus */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {/* Search */}
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                            <input
                                ref={searchInputRef}
                                type="text"
                                placeholder="Buscar por tienda, nombre o email..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                            />
                        </div>

                        {/* Status Filter */}
                        <div className="relative">
                            <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                            <select
                                value={statusFilter}
                                onChange={(e) => setStatusFilter(e.target.value)}
                                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 appearance-none"
                            >
                                <option value="all">Todos los estados</option>
                                <option value="pending">Pendientes</option>
                                <option value="verified">Aprobados</option>
                                <option value="rejected">Rechazados</option>
                            </select>
                        </div>

                        {/* Date Filter (opcional) */}
                        <div className="relative">
                            <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                            <select className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 appearance-none">
                                <option value="all">Todas las fechas</option>
                                <option value="today">Hoy</option>
                                <option value="week">Esta semana</option>
                                <option value="month">Este mes</option>
                            </select>
                        </div>
                    </div>
                </div>

                {/* Requests Table */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Solicitante
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Tienda
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Estado
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Fecha de Solicitud
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Acciones
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {requests.map((request) => (
                                    <tr key={request.id} className="hover:bg-gray-50">
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="flex items-center">
                                                <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
                                                    <span className="text-primary-600 font-medium text-sm">
                                                        {request.user?.first_name?.[0]}{request.user?.last_name?.[0]}
                                                    </span>
                                                </div>
                                                <div className="ml-4">
                                                    <div className="text-sm font-medium text-gray-900">
                                                        {request.user?.first_name} {request.user?.last_name}
                                                    </div>
                                                    <div className="text-sm text-gray-500">{request.user?.email}</div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="text-sm font-medium text-gray-900">{request.store_name}</div>
                                            <div className="text-sm text-gray-500">RUC: {request.tax_id}</div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <span className={`inline-flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium border ${getStatusSellerColor(request.verification_status)}`}>
                                                {getSellerUserStatusIcon(request.verification_status)}
                                                <span className="capitalize">{tRequestSellerStatus(request.verification_status)}</span>
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {formatDate(request.created_at)}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                            <div className="flex space-x-2">
                                                <button
                                                    onClick={() => openDetailModal(request)}
                                                    className="text-primary-600 hover:text-primary-700 p-1 rounded hover:bg-primary-50"
                                                    title="Ver detalles"
                                                >
                                                    <Eye className="w-4 h-4" />
                                                </button>

                                                {request.verification_status === 'pending' && (
                                                    <>
                                                        <button
                                                            onClick={() => handleApprove(request.id)}
                                                            disabled={processingIds.has(request.id)}
                                                            className="text-green-600 hover:text-green-700 p-1 rounded hover:bg-green-50 disabled:opacity-50"
                                                            title="Aprobar"
                                                        >
                                                            {processingIds.has(request.id) ? (
                                                                <Loader2 className="w-4 h-4 animate-spin" />
                                                            ) : (
                                                                <CheckCircle className="w-4 h-4" />
                                                            )}
                                                        </button>

                                                        <button
                                                            onClick={() => handleReject(request.id)}
                                                            disabled={processingIds.has(request.id)}
                                                            className="text-red-600 hover:text-red-700 p-1 rounded hover:bg-red-50 disabled:opacity-50"
                                                            title="Rechazar"
                                                        >
                                                            {processingIds.has(request.id) ? (
                                                                <Loader2 className="w-4 h-4 animate-spin" />
                                                            ) : (
                                                                <XCircle className="w-4 h-4" />
                                                            )}
                                                        </button>
                                                    </>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {requests.length === 0 && !isLoading && (
                        <div className="text-center py-12">
                            <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                            <h3 className="text-lg font-medium text-gray-900 mb-2">No hay solicitudes</h3>
                            <p className="text-gray-500">
                                {searchTerm || statusFilter !== 'all'
                                    ? 'No se encontraron solicitudes que coincidan con los filtros'
                                    : 'No hay solicitudes de vendedores disponibles'}
                            </p>
                        </div>
                    )}

                    {/* Pagination Controls */}
                    {requests.length > 0 && <PaginationControls />}
                </div>

                {/* Detail Modal (se mantiene igual) */}
                {showDetailModal && selectedRequest && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                        <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                            <div className="p-6 border-b border-gray-200">
                                <h2 className="text-xl font-semibold text-gray-900">
                                    Detalles de la Solicitud
                                </h2>
                            </div>

                            <div className="p-6 space-y-6">
                                {/* Información del Usuario */}
                                <div>
                                    <h3 className="text-lg font-medium text-gray-900 mb-3 flex items-center">
                                        <Users className="w-5 h-5 mr-2" />
                                        Información del Solicitante
                                    </h3>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700">Nombre</label>
                                            <p className="text-sm text-gray-900">
                                                {selectedRequest.user?.first_name} {selectedRequest.user?.last_name}
                                            </p>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700">Email</label>
                                            <p className="text-sm text-gray-900">{selectedRequest.user?.email}</p>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700">Usuario</label>
                                            <p className="text-sm text-gray-900">{selectedRequest.user?.username}</p>
                                        </div>
                                        {selectedRequest.user?.phone && (
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700">Teléfono</label>
                                                <p className="text-sm text-gray-900">{selectedRequest.user.phone}</p>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Información de la Tienda */}
                                <div>
                                    <h3 className="text-lg font-medium text-gray-900 mb-3 flex items-center">
                                        <Store className="w-5 h-5 mr-2" />
                                        Información de la Tienda
                                    </h3>
                                    <div className="space-y-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700">Nombre de la Tienda</label>
                                            <p className="text-sm text-gray-900">{selectedRequest.store_name}</p>
                                        </div>
                                        {selectedRequest.store_description && (
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700">Descripción</label>
                                                <p className="text-sm text-gray-900">{selectedRequest.store_description}</p>
                                            </div>
                                        )}
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700">RUC/DNI</label>
                                                <p className="text-sm text-gray-900">{selectedRequest.tax_id}</p>
                                            </div>
                                            {selectedRequest.business_license && (
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700">Licencia</label>
                                                    <p className="text-sm text-gray-900">{selectedRequest.business_license}</p>
                                                </div>
                                            )}
                                        </div>
                                        {selectedRequest.business_address && (
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700">Dirección</label>
                                                <p className="text-sm text-gray-900">{selectedRequest.business_address}</p>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Estado y Fechas */}
                                <div>
                                    <h3 className="text-lg font-medium text-gray-900 mb-3 flex items-center">
                                        <Calendar className="w-5 h-5 mr-2" />
                                        Estado y Fechas
                                    </h3>
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700">Estado Actual</label>
                                            <span className={`inline-flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium border ${getStatusSellerColor(selectedRequest.verification_status)}`}>
                                                {getSellerUserStatusIcon(selectedRequest.verification_status)}
                                                <span className="capitalize">{tRequestSellerStatus(selectedRequest.verification_status)}</span>
                                            </span>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700">Fecha de Solicitud</label>
                                            <p className="text-sm text-gray-900">{formatDate(selectedRequest.created_at)}</p>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700">Última Actualización</label>
                                            <p className="text-sm text-gray-900">{formatDate(selectedRequest.updated_at)}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="p-6 border-t border-gray-200 flex justify-between">
                                <button
                                    onClick={() => setShowDetailModal(false)}
                                    className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                                >
                                    Cerrar
                                </button>

                                {selectedRequest.verification_status === 'pending' && (
                                    <div className="flex space-x-3">
                                        <button
                                            onClick={() => {
                                                handleReject(selectedRequest.id);
                                                setShowDetailModal(false);
                                            }}
                                            disabled={processingIds.has(selectedRequest.id)}
                                            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors flex items-center"
                                        >
                                            {processingIds.has(selectedRequest.id) ? (
                                                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                                            ) : (
                                                <XCircle className="w-4 h-4 mr-2" />
                                            )}
                                            Rechazar
                                        </button>

                                        <button
                                            onClick={() => {
                                                handleApprove(selectedRequest.id);
                                                setShowDetailModal(false);
                                            }}
                                            disabled={processingIds.has(selectedRequest.id)}
                                            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors flex items-center"
                                        >
                                            {processingIds.has(selectedRequest.id) ? (
                                                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                                            ) : (
                                                <CheckCircle className="w-4 h-4 mr-2" />
                                            )}
                                            Aprobar
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AdminSellerRequestsComponent;